import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import {
  abandonHouseMapObjectsMapObjectIdAbandonPost,
  updateHouseInfoMapObjectsMapObjectIdHouseInfoPut,
} from '../../client';
import type { MapObject } from '../../models/mapObject';
import { normalizeMapObject } from '../../models/mapObject';
import { showAlert, showConfirm } from '../../utils/telegram/alert';
import { handleApiError } from '../../utils/net/apiError';
import { impactOccurred } from '../../utils/telegram/hapticFeedback';
import { getRadialMenuPositions } from './radialMenu';

export interface UseHouseActionsParams {
  plot: MapObject | null;
  queryClient: QueryClient;
  onPlotUpdate?: (updatedPlot: MapObject) => void;
  onClose: () => void;
  t: TFunction;
  isProcessingAction: boolean;
  setIsProcessingAction: Dispatch<SetStateAction<boolean>>;
  currentMirrorDirection: "left" | "right" | null;
  setCurrentMirrorDirection: Dispatch<SetStateAction<"left" | "right" | null>>;
  menuButtonRef: { current: HTMLDivElement | null };
  ownerMenuOptionsCount: number;
  setRadialMenu: Dispatch<SetStateAction<{ x: number; y: number } | null>>;
}

export interface UseHouseActionsResult {
  toggleRadialMenu: (open: boolean) => void;
  handleRotateHouse: () => Promise<void>;
  handleAbandonHouse: () => Promise<void>;
}

export function useHouseActions({
  plot,
  queryClient,
  onPlotUpdate,
  onClose,
  t,
  isProcessingAction,
  setIsProcessingAction,
  currentMirrorDirection,
  setCurrentMirrorDirection,
  menuButtonRef,
  ownerMenuOptionsCount,
  setRadialMenu,
}: UseHouseActionsParams): UseHouseActionsResult {
  const getClampedMenuCenter = (position: { x: number; y: number }, optionCount: number) => {
    if (typeof window === 'undefined') return position;

    // Використовуємо visualViewport якщо доступний, інакше window
    const viewport = window.visualViewport;
    const viewportLeft = viewport ? (viewport.offsetLeft + (viewport.pageLeft || 0)) : 0;
    const viewportTop = viewport ? (viewport.offsetTop + (viewport.pageTop || 0)) : 0;
    const viewportWidth = viewport?.width ?? window.innerWidth;
    const viewportHeight = viewport?.height ?? window.innerHeight;

    const positions = getRadialMenuPositions(optionCount);
    const xOffsets = positions.map((item) => item.xOffset);
    const yOffsets = positions.map((item) => item.yOffset);
    const minXOffset = xOffsets.length ? Math.min(...xOffsets) : 0;
    const maxXOffset = xOffsets.length ? Math.max(...xOffsets) : 0;
    const minYOffset = yOffsets.length ? Math.min(...yOffsets) : 0;
    const maxYOffset = yOffsets.length ? Math.max(...yOffsets) : 0;

    const safePadding = 20;
    // Button + icon group can be wide due to localized labels; use a safe estimate.
    const optionHalfWidth = 112;
    // Кнопка має висоту 30px, тому halfHeight = 15px, але також враховуємо відстань від центру
    const optionHalfHeight = 15;

    // Обмежуємо центр так, щоб всі кнопки були в межах viewport
    // Для X: центр + maxXOffset + optionHalfWidth не повинен виходити за праву межу
    // Для Y: центр + maxYOffset + optionHalfHeight не повинен виходити за нижню межу
    const minCenterX = viewportLeft + safePadding + optionHalfWidth - minXOffset;
    const maxCenterX = viewportLeft + viewportWidth - safePadding - optionHalfWidth - maxXOffset;
    const minCenterY = viewportTop + safePadding + optionHalfHeight - minYOffset;
    const maxCenterY = viewportTop + viewportHeight - safePadding - optionHalfHeight - maxYOffset;

    const clampedX = Math.min(Math.max(position.x, minCenterX), maxCenterX);
    const clampedY = Math.min(Math.max(position.y, minCenterY), maxCenterY);

    return { x: clampedX, y: clampedY };
  };

  const toggleRadialMenu = (open: boolean) => {
    if (!open) {
      setRadialMenu(null);
      return;
    }

    if (!menuButtonRef.current) return;
    const rect = menuButtonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const clampedCenter = getClampedMenuCenter({ x: centerX, y: centerY }, ownerMenuOptionsCount);
    setRadialMenu(clampedCenter);
  };

  const applyLocalMirrorDirection = (direction: "left" | "right") => {
    if (!plot) return;

    setCurrentMirrorDirection(direction);

    queryClient.setQueryData<MapObject[]>(['mapObjects'], (old) => {
      if (!old) return old;
      return old.map((obj) =>
        obj.id === plot.id ? { ...obj, mirror_direction: direction } : obj
      );
    });

    if (onPlotUpdate) {
      onPlotUpdate({ ...plot, mirror_direction: direction });
    }
  };

  // Обробник ротації будинку (перемикає між left і right)
  const handleRotateHouse = async () => {
    if (!plot || isProcessingAction) return;
    
    // Визначаємо поточний напрямок і перемикаємо на протилежний
    const currentDirection = currentMirrorDirection || plot.mirror_direction || 'left';
    const newDirection: "left" | "right" = currentDirection === 'left' ? 'right' : 'left';
    
    setIsProcessingAction(true);
    try {
      await updateHouseInfoMapObjectsMapObjectIdHouseInfoPut({
        path: { map_object_id: plot.id },
        body: { mirror_direction: newDirection },
        throwOnError: true,
      });
      applyLocalMirrorDirection(newDirection);
      queryClient.invalidateQueries({ queryKey: ['mapObjects'] });
      impactOccurred('medium');
    } catch (error: unknown) {
      console.error('[RealEstateInfoModal] Error rotating house:', error);
      const apiErr = handleApiError(error, { logLabel: 'setHouseMirrorDirection' });
      showAlert(apiErr.detail || t('Failed to rotate house'));
    } finally {
      setIsProcessingAction(false);
    }
  };

  // Обробник відмови від будинку
  const handleAbandonHouse = async () => {
    if (!plot || isProcessingAction) return;
    
    const confirmed = await showConfirm(t('Are you sure you want to abandon this house? It will be converted back to a free plot and any player will be able to claim it.'));
    if (!confirmed) {
      return;
    }
    
    setIsProcessingAction(true);
    try {
      const { data: response } = await abandonHouseMapObjectsMapObjectIdAbandonPost({
        path: { map_object_id: plot.id },
        throwOnError: true,
      });
      const updatedMapObject = normalizeMapObject(response.map_object);
      // Оновлюємо кеш
      queryClient.setQueryData<MapObject[]>(['mapObjects'], (old) => {
        if (!old) return old;
        return old.map((obj) =>
          obj.id === plot.id ? updatedMapObject : obj
        );
      });
      if (onPlotUpdate) {
        onPlotUpdate(updatedMapObject);
      }
      impactOccurred('medium');
      onClose();
    } catch (error: unknown) {
      console.error('[RealEstateInfoModal] Error abandoning house:', error);
      const apiErr = handleApiError(error, { logLabel: 'abandonHouse' });
      showAlert(apiErr.detail || t('Failed to abandon house'));
    } finally {
      setIsProcessingAction(false);
    }
  };

  return {
    toggleRadialMenu,
    handleRotateHouse,
    handleAbandonHouse,
  };
}
